import type { QueryClient } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import type { PageDescriptor } from "@pm2/api";
import { extractElementIdFromPath } from "@pm2/api";
import { HttpStatus, withSuspenseCache } from "@pm2/react-utils";
import type { PM2SitemapStore } from "@pm2/sitemap-utils";
import {
  getRenderedDescriptorByPath,
  pm2SitemapStore,
} from "@pm2/sitemap-utils";
import type { PM2ConfigStore } from "@pm2/store";
import { pm2ConfigStore } from "@pm2/store";
import { NotFound } from "@pm2/ui-components";
import type { UserIdentity } from "@provider/user-identity";
import { useUserIdentityState } from "@provider/user-identity";
import { createCacheWrapper } from "@utils/cache";
import { asyncTimeout, isPlatformNodeJS } from "@utils/common";
import { DAYS_TO_MS } from "@utils/constants";

import { skipLinkSelectors } from "./atom.skip-link.styles";
import { renderContentPage } from "./page.content";
import { renderCourseFrontpage } from "./page.course-frontpage";
import { renderElementPage } from "./page.element";
import { renderLibrary } from "./page.library";
import { renderResourcePage } from "./page.resource";
import { renderSearchPage } from "./page.search";
import { renderBookShelf } from "./page.shelf";
import { renderTeacherGuidePage } from "./page.teacher-guide";

const TRANSITION_DELAY_MS = 100;

const cache = createCacheWrapper({
  timeToLiveMs: 1 * DAYS_TO_MS,
  capacity: 100,

  // we intentionally retain errors in the cache, as we need to avoid cases
  // where an error triggers an infinite loop where we reach out to the API
  // over and over again
  retainErrors: true,
});

type Props = {
  serverSide?: boolean;
  asPath: string;
  pathname: string;
  searchParams: URLSearchParams;

  /**
   * will be triggered when the page is ready to render (please note that when
   * this callback is specified, no content will ever actually render - the
   * element instance will simply serve as a placeholder to use Suspence for
   * data fetching).
   */
  onLoad?(asPath: string): void;
};

let isSwitchingPage = false;
let firstPagePath: string | undefined;

/**
 * wraps the page content component, making sure that all data needed for
 * rendering is fetched before it is mounted.
 */
export function PageResolver(props: Props): JSX.Element | null {
  const queryClient = useQueryClient();

  const configStore = pm2ConfigStore.useStore();
  const sitemapStore = pm2SitemapStore.useStore();

  const isSitemapResolved = pm2SitemapStore.useSelector(
    (state) => state.isSitemapResolved,
  );
  const hasRenderedFirstPage = pm2SitemapStore.useSelector(
    (state) => state.hasRenderedFirstPage,
  );

  const user = useUserIdentityState((state) => state.user);

  // Ensure that the sitemap has been fully resolved for the current user before
  // displaying this page as the server-side prepared sitemap may contain
  // invalid access control.
  if (!isPlatformNodeJS() && !isSitemapResolved) {
    throw asyncTimeout(10);
  }

  // resolve reference to the page that we should display
  const elementId = extractElementIdFromPath(props.pathname);
  const resourceId = props.searchParams.get("resource") ?? undefined;
  const pageDescriptor = !elementId
    ? withSuspenseCache(
        getRenderedDescriptorByPath(configStore, sitemapStore, props.pathname),
      )
    : undefined;

  // render content based on resolved references
  const content = withSuspenseCache(
    renderPage(queryClient, user, configStore, sitemapStore, props, {
      pageDescriptor,
      elementId,
      resourceId,
    }),
  );

  useEffect(
    () => {
      // We can only ever properly render pages once the user has been fully
      // loaded as this may influence access control, page loading etc.
      if (user === undefined) {
        return;
      }

      // scroll to top of page every time the user changes page after the new
      // page is actually fading into view
      const scrollTimeout = window.setTimeout(() => {
        window.scrollTo(0, 0);
      }, TRANSITION_DELAY_MS - 75);

      const focusTimeout = window.setTimeout(() => {
        // NB: On the advanced search page specifically we will want to move
        // our focus directly to the search input, as people will frequently
        // visit this pages using the QuickSearch by hitting 'Enter'. This
        // group of users are not normally accustomed to skip-links, and as such
        // we instead allow them to immediately continue their search journey.
        if (
          isSwitchingPage &&
          !props.asPath.includes(sitemapStore.getState().searchPath)
        ) {
          document
            .querySelector<HTMLAnchorElement>(skipLinkSelectors.anchor())
            ?.focus({ preventScroll: true });
        }
      }, TRANSITION_DELAY_MS + 75);

      // trigger onLoad callback when we've successfully loaded the content on
      // the current page
      props.onLoad?.(props.asPath);

      return () => {
        window.clearTimeout(scrollTimeout);
        window.clearTimeout(focusTimeout);
      };
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user],
  );

  useEffect(() => {
    if (hasRenderedFirstPage && !firstPagePath) {
      firstPagePath = props.asPath;
    }

    if (hasRenderedFirstPage && firstPagePath !== props.asPath) {
      isSwitchingPage = true;
    }
  }, [hasRenderedFirstPage, props.asPath]);

  // only render content externally if the onLoad prop wasn't supplied (as in
  // that case we only wish to )
  return props.onLoad === undefined ? content : null;
}

function renderPage(
  queryClient: QueryClient,
  user: UserIdentity | null | undefined,
  configStore: PM2ConfigStore,
  sitemapStore: PM2SitemapStore,
  props: Props,
  reference: {
    pageDescriptor: PageDescriptor | undefined;
    elementId: string | undefined;
    resourceId: string | undefined;
  },
): Promise<JSX.Element | null> | JSX.Element | null {
  if (props.serverSide) {
    // prevent actual content rendering on server side
    return !reference.pageDescriptor && !reference.elementId ? (
      <HttpStatus code={404} />
    ) : null;
  }

  const cacheKey = [
    user ? user.id : user === null ? "not-logged-in" : "pending",
    reference.pageDescriptor?.id,
    reference.elementId,
    reference.resourceId,
  ].join(";");

  const configState = configStore.getState();

  return cache(cacheKey, async () => {
    if (!reference.pageDescriptor && !reference.elementId) {
      return <NotFound />;
    }

    if (reference.elementId !== undefined) {
      return renderElementPage(
        queryClient,
        configStore,
        user,
        reference.elementId,
      );
    }

    if (
      reference.resourceId !== undefined &&
      reference.pageDescriptor !== undefined
    ) {
      return renderResourcePage(
        queryClient,
        configStore,
        sitemapStore,
        user,
        reference.resourceId,
        reference.pageDescriptor,
      );
    }

    switch (reference.pageDescriptor?.type) {
      case "Shelf":
        return renderBookShelf(user, configStore, reference.pageDescriptor);

      case "Library":
        return renderLibrary(
          queryClient,
          configStore,
          reference.pageDescriptor,
        );

      case "SiteSearch":
        return renderSearchPage(configStore, props.searchParams.get("q") ?? "");

      case "Course":
        return configState.config.settings.showCourseFrontpages
          ? renderCourseFrontpage(
              user,
              configStore,
              sitemapStore,
              queryClient,
              reference.pageDescriptor,
            )
          : renderContentPage(
              user,
              configStore,
              sitemapStore,
              queryClient,
              reference.pageDescriptor,
            );

      case "TeachersGuidePage":
        return renderTeacherGuidePage(
          user,
          configStore,
          sitemapStore,
          reference.pageDescriptor,
        );

      default:
        return reference.pageDescriptor ? (
          renderContentPage(
            user,
            configStore,
            sitemapStore,
            queryClient,
            reference.pageDescriptor,
          )
        ) : (
          <NotFound />
        );
    }
  }).getStaleWhileRevalidate();
}
